<div class="requestor-portal-page">
  <blocking-loader-overlay
    :show="page_loading"
    :message="page_loading_message"
    :detail="page_loading_detail"
  />
  <div class="requestor-portal-shell mx-auto">
    <section class="requestor-portal-profile border d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
      <div class="d-flex align-items-center gap-3 min-w-0">
        <div class="requestor-portal-avatar rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0">
          {{ requestor_initials }}
        </div>

        <div class="min-w-0">
          <h1 class="requestor-portal-name mb-1">{{ requestor_name }}</h1>
          <div class="requestor-portal-meta text-uppercase">
            Policy {{ policy_number }} <span class="mx-2">-</span> Member since {{ member_since }}
          </div>
        </div>
      </div>

      <button type="button" class="requestor-portal-signout btn btn-light border px-3" @click="sign_out">
        Sign out
      </button>
    </section>

    <section class="requestor-portal-verify border d-flex align-items-center gap-3">
      <div class="requestor-portal-lock d-inline-flex align-items-center justify-content-center flex-shrink-0">
        <i class="bi bi-lock"></i>
      </div>

      <div class="flex-grow-1 min-w-0">
        <div class="requestor-portal-verify-title fw-bold">Verify your ID to unlock everything.</div>
        <div class="requestor-portal-copy">
          Verified users can submit new claims, see all previous cases, and update payout details. Takes 30 seconds.
        </div>
      </div>

      <button type="button" class="requestor-portal-dark-btn btn border-0 px-4 flex-shrink-0" @click="open_verify_modal">
        Verify now
      </button>

      <button type="button" class="requestor-portal-icon-btn btn border-0 p-0 flex-shrink-0" aria-label="Dismiss verification reminder">
        <i class="bi bi-x-lg"></i>
      </button>
    </section>

    <section class="requestor-portal-tabs border d-grid">
      <button
        type="button"
        class="requestor-portal-tab btn border-0 rounded-0 fw-semibold"
        :class="{ 'is-active shadow': active_tab === 'active' }"
        @click="active_tab = 'active'"
      >
        Active cases
        <span class="requestor-portal-count ms-2" :class="{ 'is-muted': active_tab !== 'active' }">{{ active_case_count }}</span>
      </button>
      <button
        type="button"
        class="requestor-portal-tab btn border-0 rounded-0 fw-semibold "
        :class="{ 'is-active shadow': active_tab === 'previous' }"
        @click="active_tab = 'previous'"
      >
        Previous cases
        <span class="requestor-portal-count ms-2" :class="{ 'is-muted': active_tab !== 'previous shadow' }">{{ previous_case_count }}</span>
      </button>
    </section>

    <div v-if="active_tab === 'active'" class="requestor-portal-flow">
    <section class="requestor-portal-section-head d-flex align-items-end justify-content-between gap-3">
      <h2 class="requestor-portal-section-title mb-0">Your Open Cases</h2>
      <div class="requestor-portal-meta text-uppercase">{{ active_cases_summary }}</div>
    </section>

    <div v-if="portal_error" class="requestor-portal-empty border mb-3">
      {{ portal_error }}
    </div>

    <div v-else-if="!loading_open_cases && !active_cases.length" class="requestor-portal-empty border mb-3">
      No open cases found for this project yet.
    </div>

    <article
      v-for="case_item in active_cases"
      :key="case_item.id"
      class="requestor-portal-case border mb-3"
      :class="{ 'requestor-portal-case-warning': case_item.status_class === 'is-warning' }"
    >
      <header class="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div class="min-w-0">
          <div class="d-flex align-items-center gap-2 flex-wrap mb-3">
            <span class="requestor-portal-type text-uppercase" :class="case_item.type_class">{{ case_item.type_label }}</span>
            <span class="requestor-portal-case-meta">
              {{ case_item.code }} - opened {{ case_item.opened }}
            </span>
            <span v-if="case_item.status" class="requestor-portal-status" :class="case_item.status_class">
              <i v-if="case_item.status_class === 'is-warning'" class="bi bi-exclamation-triangle"></i>
              <span v-else class="requestor-portal-dot"></span>
              {{ case_item.status }}
            </span>
          </div>
          <h3 class="requestor-portal-case-title mb-0">{{ case_item.title }}</h3>
        </div>

        <button
          type="button"
          class="requestor-portal-icon-btn btn border-0 p-0"
          :aria-label="is_case_expanded(case_item) ? 'Collapse case' : 'Expand case'"
          @click="toggle_case(case_item)"
        >
          <i class="bi" :class="is_case_expanded(case_item) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </button>
      </header>

      <div class="requestor-portal-progress mb-4" :class="case_item.progress_class" aria-hidden="true">
        <span class="requestor-portal-progress-line"></span>
        <span
          v-for="(step, index) in case_item.progress_steps"
          :key="`${case_item.id}-step-${index}`"
          class="requestor-portal-step"
          :class="`is-${step}`"
        >
          <i v-if="step === 'complete'" class="bi bi-check"></i>
        </span>
      </div>

      <div v-if="case_item.action_needed" class="requestor-portal-action-needed d-flex align-items-center gap-3">
        <span class="requestor-portal-action-icon rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0">!</span>
        <div class="min-w-0 flex-grow-1">
          <div class="fw-bold mb-1">{{ case_item.action_needed.title }}</div>
          <div>{{ case_item.action_needed.body }}</div>
        </div>
        <button type="button" class="requestor-portal-dark-btn btn border-0 px-4 flex-shrink-0">{{ case_item.action_needed.button }}</button>
      </div>

      <div v-else class="requestor-portal-flow">
      <div class="requestor-portal-update d-flex align-items-start gap-3 mb-4">
        <span class="requestor-portal-update-dot rounded-circle flex-shrink-0"></span>
        <div>
          <div class="fw-bold mb-1">{{ case_item.update_title }}</div>
          <div class="requestor-portal-copy">{{ case_item.update_body }}</div>
        </div>
      </div>

      <div v-if="is_case_expanded(case_item)" class="requestor-portal-flow">
      <div v-if="case_item.details_loading" class="requestor-portal-empty border mb-3">
        Loading case details...
      </div>
      <template v-else>
      <div class="requestor-portal-facts row g-3 py-4 mb-4">
        <div v-for="fact in case_item.facts" :key="fact.label" class="col-12 col-md-4">
          <div class="requestor-portal-label text-uppercase mb-1">{{ fact.label }}</div>
          <div class="requestor-portal-fact-value">{{ fact.value }}</div>
        </div>
      </div>

      <div class="requestor-portal-label text-uppercase mb-4">Progress so far</div>
      <div class="requestor-portal-timeline d-flex flex-column">
        <div v-for="event in case_item.events" :key="event.title" class="requestor-portal-event d-flex align-items-start gap-3">
          <span class="requestor-portal-event-icon rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="`is-${event.state}`">
            <i v-if="event.state === 'complete'" class="bi bi-check"></i>
          </span>
          <div>
            <div class="requestor-portal-event-title">{{ event.title }}</div>
            <div class="requestor-portal-copy">{{ event.meta }}</div>
          </div>
        </div>
      </div>

      <div v-for="answer_section in case_item.case_fact_sections" :key="answer_section.id || answer_section.name" class="requestor-portal-flow">
      <section v-if="has_visible_answer_section(answer_section, case_item)" class="requestor-portal-file mt-4">
        <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
          <div v-if="answer_section.name" class="requestor-portal-label text-uppercase mb-0">{{ answer_section.name }}</div>
          <button
            v-if="can_edit_answer_section(answer_section) && !is_editing_case_section(case_item, answer_section)"
            type="button"
            class="requestor-portal-icon-btn btn border-0 p-0 flex-shrink-0"
            aria-label="Edit section"
            @click="begin_case_section_edit(case_item, answer_section)"
          >
            <i class="bi bi-pencil"></i>
          </button>
        </div>

        <div v-if="case_section_edit_error && is_editing_case_section(case_item, answer_section)" class="requestor-portal-empty border mb-3">
          {{ case_section_edit_error }}
        </div>
        <div v-if="case_section_edit_success" class="requestor-portal-empty border mb-3">
          {{ case_section_edit_success }}
        </div>

        <div v-if="answer_section_facts(answer_section).length" class="requestor-portal-file-grid border">
          <div
            v-for="fact in answer_section_facts(answer_section)"
            :key="fact.label"
            class="requestor-portal-file-cell"
            :class="{ 'requestor-portal-file-statement': is_long_text_case_fact(fact) }"
          >
            <div class="requestor-portal-label text-uppercase mb-2">{{ fact.label }}</div>
            <template v-if="is_editing_case_section(case_item, answer_section) && can_edit_case_fact(fact)">
              <select
                v-if="is_select_case_fact(fact)"
                :value="editing_case_fact_value(fact)"
                class="form-select requestor-portal-date w-100"
                @change="update_case_fact_value(fact, $event.target.value)"
              >
                <option value="">Select an option</option>
                <option v-for="option in fact.options" :key="option" :value="option">{{ option }}</option>
              </select>

              <select
                v-else-if="is_multi_select_case_fact(fact)"
                :value="editing_case_fact_values(fact)"
                class="form-select requestor-portal-date w-100"
                multiple
                @change="update_case_fact_multi_value(fact, $event.target.selectedOptions)"
              >
                <option v-for="option in fact.options" :key="option" :value="option">{{ option }}</option>
              </select>

              <div v-else-if="is_checkbox_case_fact(fact)" class="d-flex flex-column gap-2">
                <label v-for="option in fact.options" :key="option" class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="option"
                    :checked="editing_case_fact_values(fact).includes(option)"
                    @change="update_case_fact_checkbox_value(fact, option, $event.target.checked)"
                  >
                  <span class="form-check-label requestor-portal-copy">{{ option }}</span>
                </label>
              </div>

              <div v-else-if="is_radio_case_fact(fact)" class="d-flex flex-column gap-2">
                <label v-for="option in fact.options" :key="option" class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    :name="`${case_item.id}-${answer_section.id}-${fact.label}`"
                    :value="option"
                    :checked="editing_case_fact_value(fact) === option"
                    @change="update_case_fact_value(fact, $event.target.value)"
                  >
                  <span class="form-check-label requestor-portal-copy">{{ option }}</span>
                </label>
              </div>

              <textarea
                v-else-if="is_long_text_case_fact(fact)"
                :value="editing_case_fact_value(fact)"
                class="form-control requestor-portal-textarea"
                rows="4"
                @input="update_case_fact_value(fact, $event.target.value)"
              ></textarea>

              <input
                v-else
                :value="editing_case_fact_value(fact)"
                :type="input_type_for_case_fact(fact)"
                class="form-control requestor-portal-date w-100"
                @input="update_case_fact_value(fact, $event.target.value)"
              >
            </template>
            <p v-else-if="is_long_text_case_fact(fact)" class="requestor-portal-file-value mb-0">{{ fact.value }}</p>
            <div v-else class="requestor-portal-file-value">{{ fact.value }}</div>
          </div>
        </div>

        <div v-if="is_editing_case_section(case_item, answer_section)" class="d-flex align-items-center gap-2 flex-wrap mt-3">
          <button
            type="button"
            class="requestor-portal-dark-btn btn border-0 px-3"
            :disabled="is_saving_case_section(case_item, answer_section)"
            @click="save_case_section_edit(case_item, answer_section)"
          >
            {{ is_saving_case_section(case_item, answer_section) ? 'Saving...' : 'Save section' }}
          </button>
          <button
            type="button"
            class="requestor-portal-light-btn btn border px-3"
            :disabled="is_saving_case_section(case_item, answer_section)"
            @click="cancel_case_section_edit"
          >
            Cancel
          </button>
        </div>

        <div class="d-flex flex-column gap-2 mt-3">
          <article
            v-for="document in answer_section_documents(answer_section, case_item)"
            :key="document.id"
            class="requestor-portal-doc border d-flex align-items-center gap-3"
          >
            <span class="requestor-portal-doc-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="document.tone">
              <i class="bi" :class="document.icon"></i>
            </span>
            <div class="requestor-portal-doc-body min-w-0 flex-grow-1">
              <div class="requestor-portal-doc-title text-truncate">{{ document.title }}</div>
              <div class="requestor-portal-case-meta">{{ document.meta }}</div>
              <div v-if="document.verification_label" class="requestor-portal-copy small mt-1">
                Status : {{ document.verification_label }}
              </div>
            </div>
            <button
              type="button"
              class="requestor-portal-link-btn btn border-0 p-0 flex-shrink-0"
              @click="open_document_preview(document)"
            >
              Preview &rarr;
            </button>
          </article>
        </div>
      </section>
      </div>

      <section class="requestor-portal-docs mt-4">
        <div class="requestor-portal-label text-uppercase mb-3">Documents</div>
        <div class="d-flex flex-column gap-2">
          <div v-if="!case_item.documents.length" class="requestor-portal-empty border">
            No documents were found for this case.
          </div>
          <article v-for="document in case_item.documents" :key="document.title" class="requestor-portal-doc border d-flex align-items-center gap-3">
            <span class="requestor-portal-doc-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="document.tone">
              <i class="bi" :class="document.icon"></i>
            </span>
            <div class="requestor-portal-doc-body min-w-0 flex-grow-1">
              <div class="requestor-portal-doc-title fw-semibold text-truncate">{{ document.title }}</div>
              <div class="requestor-portal-case-meta">{{ document.meta }}</div>
              <div v-if="document.verification_label" class="requestor-portal-copy small mt-1">
                Status : {{ document.verification_label }}
              </div>
            </div>
            <button type="button" class="requestor-portal-link-btn btn border-0 p-0 flex-shrink-0" @click="open_document_preview(document)">Preview &rarr;</button>
          </article>
        </div>

        <div class="requestor-portal-doc-actions d-flex align-items-center gap-2 flex-wrap mt-3 mb-3">
          <div
            v-if="case_document_upload_targeted(case_item) && case_document_upload_error"
            class="requestor-portal-empty border w-100"
          >
            {{ case_document_upload_error }}
          </div>
          <div
            v-if="case_document_upload_targeted(case_item) && case_document_upload_success"
            class="requestor-portal-empty border w-100"
          >
            {{ case_document_upload_success }}
          </div>
          <button
            v-for="action in case_item.actions"
            :key="action"
            type="button"
            class="requestor-portal-doc-action btn px-3"
            :class="action === 'Message handler' ? 'requestor-portal-dark-btn border-0' : 'requestor-portal-light-btn border'"
            :disabled="uploading_case_document && is_upload_documents_action(action) && case_document_upload_targeted(case_item)"
            @click="is_upload_documents_action(action) ? choose_case_document(case_item) : null"
          >
            {{ uploading_case_document && is_upload_documents_action(action) && case_document_upload_targeted(case_item) ? 'Uploading...' : action }}
          </button>
        </div>
      </section>
      </template>
      </div>
      </div>
    </article>
    </div>

    <div v-else class="requestor-portal-flow">
      <section class="requestor-portal-section-head d-flex align-items-end justify-content-between gap-3">
        <h2 class="requestor-portal-section-title mb-0">Previous cases</h2>
        <div class="requestor-portal-meta text-uppercase">Last 24 months</div>
      </section>

      <div class="d-flex flex-column gap-2">
        <div v-if="!loading_open_cases && !previous_cases.length" class="requestor-portal-empty border mb-3">
          No closed cases found for this project yet.
        </div>

        <article
          v-for="case_item in previous_cases"
          :key="case_item.code"
          class="requestor-portal-previous border d-grid align-items-center gap-3"
          role="button"
          tabindex="0"
          :aria-label="`View details for ${case_item.code}`"
          @click="open_previous_case_modal(case_item)"
          @keydown.enter.prevent="open_previous_case_modal(case_item)"
          @keydown.space.prevent="open_previous_case_modal(case_item)"
        >
          <div class="requestor-portal-case-meta">{{ case_item.code }}</div>
          <div class="min-w-0">
            <div class="requestor-portal-doc-title fw-semibold text-truncate">{{ case_item.title }}</div>
            <div class="requestor-portal-copy">{{ case_item.meta }}</div>
          </div>
          <span v-if="case_item.status" class="requestor-portal-previous-status text-uppercase mb-5" :class="case_item.tone">{{ case_item.status }}</span>
          <div class="text-nowrap text-xs mb-5">{{ case_item.date }}</div>
        </article>
      </div>
    </div>

    <footer class="requestor-portal-footer text-center">
      Questions? Call <strong>011 555 0142</strong> - WhatsApp <strong>081 555 0142</strong> with your reference.
    </footer>
  </div>

  <button type="button" class="requestor-portal-floating btn border-0 d-inline-flex align-items-center gap-2" @click="open_new_case_modal">
    <i class="bi bi-plus-lg"></i>
    <span>Open a new case</span>
  </button>

  <div v-if="show_verify_modal" class="requestor-portal-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_verify_modal">
    <section class="requestor-portal-modal requestor-portal-verify-modal bg-white">
      <header class="requestor-portal-modal-header requestor-portal-verify-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="requestor-portal-section-title mb-2">Verify it's you</h2>
          <div class="requestor-portal-copy">Once verified, your previous cases will appear and new submissions go through faster.</div>
        </div>
        <button type="button" class="requestor-portal-icon-btn btn border-0 p-0 flex-shrink-0" aria-label="Close verification modal" @click="close_verify_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="requestor-portal-modal-body requestor-portal-verify-modal-body">
        <div class="requestor-portal-label text-uppercase mb-3">SA ID number</div>
        <input
          v-model.trim="verify_id_number"
          type="text"
          class="form-control requestor-portal-verify-input mb-4"
          placeholder="9001015001087"
        >

        <div class="requestor-portal-label text-uppercase mb-3">We'll send a one-time code to</div>
        <div class="requestor-portal-verify-channel-grid d-grid gap-3">
          <label class="requestor-portal-verify-channel" :class="{ 'is-selected': verify_contact_channel === 'sms' }">
            <input v-model="verify_contact_channel" class="requestor-portal-verify-channel-input" type="radio" value="sms">
            <span class="requestor-portal-verify-channel-copy">
              <span class="requestor-portal-verify-channel-title">SMS</span>
              <span class="requestor-portal-copy">+27 82 ••• 0142</span>
            </span>
          </label>

          <label class="requestor-portal-verify-channel" :class="{ 'is-selected': verify_contact_channel === 'whatsapp' }">
            <input v-model="verify_contact_channel" class="requestor-portal-verify-channel-input" type="radio" value="whatsapp">
            <span class="requestor-portal-verify-channel-copy">
              <span class="requestor-portal-verify-channel-title">WhatsApp</span>
              <span class="requestor-portal-copy">+27 82 ••• 0142</span>
            </span>
          </label>
        </div>

        <div class="requestor-portal-verify-note d-flex align-items-start gap-3 mt-4">
          <span class="requestor-portal-verify-note-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
            <i class="bi bi-lock"></i>
          </span>
          <div class="requestor-portal-copy mb-0">
            We check your ID against Home Affairs records. Your ID number is never stored - only the verification result. POPIA-compliant.
          </div>
        </div>
      </div>

      <footer class="requestor-portal-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="requestor-portal-light-btn btn border px-4" @click="close_verify_modal">Skip for now</button>
        <button type="button" class="requestor-portal-dark-btn btn border-0 px-4">Send OTP</button>
      </footer>
    </section>
  </div>

  <div v-if="show_new_case_modal" class="requestor-portal-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_new_case_modal">
    <section class="requestor-portal-modal bg-white">
      <header class="requestor-portal-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="requestor-portal-section-title mb-2">Open a new case</h2>
          <div class="requestor-portal-copy">Tell us what's happened. We'll route it to the right team within an hour.</div>
        </div>
        <button type="button" class="requestor-portal-icon-btn btn border-0 p-0 flex-shrink-0" aria-label="Close modal" @click="close_new_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="requestor-portal-modal-body">
        <div class="requestor-portal-label text-uppercase mb-3">What type of case is this?</div>

        <div
          v-if="modal_error"
          ref="newCaseError"
          class="requestor-portal-empty border mb-3"
          role="alert"
          tabindex="-1"
        >
          {{ modal_error }}
        </div>

        <div class="requestor-portal-case-types d-grid gap-2 mb-4">
          <button
            v-for="case_type in case_types"
            :key="case_type.id"
            type="button"
            class="requestor-portal-case-type btn text-start border"
            :class="{ 'is-selected': Number(selected_entity_type_id) === Number(case_type.id) }"
            @click="select_case_type(case_type.id)"
          >
            <span class="requestor-portal-radio rounded-circle" :class="{ 'is-selected': Number(selected_entity_type_id) === Number(case_type.id) }"></span>
            <span>
              <span class="d-block fw-bold text-uppercase">{{ case_type.title }}</span>
              <span class="requestor-portal-label text-uppercase">{{ case_type.meta }}</span>
            </span>
          </button>
        </div>

        <div v-if="!loading_case_form && selected_entity_type_id && !section_cards.length && !modal_error" class="requestor-portal-empty border mb-3">
          No form fields were returned for this case type.
        </div>

        <configured-case-form
          v-else-if="!loading_case_form"
          :sections="section_cards"
          :answer-values="answer_values"
          :validation-errors="validation_errors"
          id-prefix="requestor-new-case"
          @answer-change="update_configured_form_answer"
        ></configured-case-form>
      </div>

      <footer class="requestor-portal-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="requestor-portal-light-btn btn border px-4" @click="close_new_case_modal">Cancel</button>
        <button
          type="button"
          class="requestor-portal-dark-btn btn border-0 px-4"
          :disabled="submitting_new_case || loading_case_form || !can_submit_new_case"
          @click="submit_new_case"
        >
          {{ submitting_new_case ? 'Opening...' : 'Open the case' }}
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_previous_case_modal" class="requestor-portal-modal-backdrop requestor-portal-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_previous_case_modal">
    <section class="requestor-portal-case-modal bg-white">
      <header class="requestor-portal-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
            <span class="requestor-portal-type text-uppercase">{{ selected_previous_case.type_label }}</span>
            <span class="requestor-portal-case-meta">{{ selected_previous_case.code }}</span>
            <span v-if="selected_previous_case.status" class="requestor-portal-previous-status text-uppercase" :class="selected_previous_case.tone">
              {{ selected_previous_case.status }}
            </span>
          </div>
          <h2 class="requestor-portal-section-title mb-2">{{ selected_previous_case.title }}</h2>
          <div class="requestor-portal-copy">
            Opened {{ selected_previous_case.opened }}<template v-if="selected_previous_case.status"> <span class="mx-1">-</span> Latest status {{ selected_previous_case.status }}</template>
          </div>
        </div>
        <button type="button" class="requestor-portal-icon-btn btn border-0 p-0 flex-shrink-0" aria-label="Close case details" @click="close_previous_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="requestor-portal-case-modal-body">
        <div v-if="selected_previous_case.details_loading" class="requestor-portal-empty border mb-3">
          Loading case details...
        </div>
        <template v-else>
        <section v-if="selected_previous_case.status" class="requestor-portal-outcome-card border mb-4" :class="selected_previous_case.tone">
          <div class="d-flex align-items-start gap-3">
            <span class="requestor-portal-outcome-icon rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0">
              <i class="bi bi-check-lg"></i>
            </span>
            <div>
              <div class="requestor-portal-outcome-title fw-bold mb-2">{{ selected_previous_case.status }}</div>
              <div class="requestor-portal-copy">{{ selected_previous_case.update_body }}</div>
            </div>
          </div>
        </section>

        <section class="mb-4">
          <div class="requestor-portal-label text-uppercase mb-3">What this case was about</div>
          <div class="requestor-portal-summary-card">
            {{ previous_case_about }}
          </div>
        </section>

        <section v-if="previous_case_fact_grid.length" class="mb-4">
          <div class="requestor-portal-label text-uppercase mb-3">Case facts</div>
          <div class="requestor-portal-file-grid border">
            <div
              v-for="fact in previous_case_fact_grid"
              :key="fact.label"
              class="requestor-portal-file-cell"
              :class="{ 'requestor-portal-file-statement': is_long_text_case_fact(fact) }"
            >
              <div class="requestor-portal-label text-uppercase mb-2">{{ fact.label }}</div>
              <p v-if="is_long_text_case_fact(fact)" class="requestor-portal-file-value mb-0">{{ fact.value }}</p>
              <div v-else class="requestor-portal-file-value">{{ fact.value }}</div>
            </div>
          </div>
        </section>

        <section class="mb-4">
          <div class="requestor-portal-label text-uppercase mb-3">Progress</div>
          <div class="requestor-portal-timeline d-flex flex-column">
            <div v-for="event in selected_previous_case.events" :key="event.title" class="requestor-portal-event d-flex align-items-start gap-3">
              <span class="requestor-portal-event-icon rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="`is-${event.state}`">
                <i v-if="event.state === 'complete' || event.state === 'current'" class="bi bi-check"></i>
              </span>
              <div>
                <div class="requestor-portal-event-title fw-semibold">{{ event.title }}</div>
                <div class="requestor-portal-copy">{{ event.meta }}</div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div class="requestor-portal-label text-uppercase mb-3">
            Documents ({{ previous_case_documents.length }}) <span class="ms-2">Tap to preview</span>
          </div>
          <div class="d-flex flex-column gap-2">
            <div v-if="!previous_case_documents.length" class="requestor-portal-empty border">
              No documents were found for this case.
            </div>
            <article v-for="document in previous_case_documents" :key="document.id || document.title" class="requestor-portal-doc border d-flex align-items-center gap-3">
              <span class="requestor-portal-doc-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="document.tone">
                <i class="bi" :class="document.icon"></i>
              </span>
              <div class="requestor-portal-doc-body min-w-0 flex-grow-1">
                <div class="requestor-portal-doc-title fw-semibold text-truncate">{{ document.title }}</div>
                <div class="requestor-portal-case-meta">{{ document.meta }}</div>
                <div v-if="document.verification_label" class="requestor-portal-copy small mt-1">
                  Status : {{ document.verification_label }}
                </div>
              </div>
              <button type="button" class="requestor-portal-link-btn btn border-0 p-0 flex-shrink-0" @click="open_document_preview(document)">Preview &rarr;</button>
            </article>
          </div>
        </section>
        </template>
      </div>
    </section>
  </div>

  <div v-if="show_document_drawer" class="requestor-portal-drawer-backdrop" @click.self="close_document_preview">
    <aside class="requestor-portal-drawer bg-white">
      <header class="requestor-portal-drawer-header d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <h2 class="requestor-portal-doc-title fw-bold mb-1 text-truncate">{{ selected_document.title }}</h2>
          <div class="requestor-portal-case-meta">{{ selected_document.meta }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <button
            v-if="can_resubmit_document(selected_document)"
            type="button"
            class="requestor-portal-dark-btn btn border-0 px-3"
            :disabled="replacing_document"
            @click="choose_replacement_document"
          >
            {{ replacing_document ? 'Uploading...' : 'Upload replacement' }}
          </button>
          <button
            v-if="selected_document.url"
            type="button"
            class="requestor-portal-light-btn btn border px-3"
            @click="download_document(selected_document)"
          >
            Download
          </button>
          <button type="button" class="requestor-portal-icon-btn btn border-0 p-0" aria-label="Close document preview" @click="close_document_preview">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </header>

      <div class="requestor-portal-drawer-body">
        <div
          v-if="document_replace_error"
          class="requestor-portal-empty border mb-3"
        >
          {{ document_replace_error }}
        </div>
        <div
          v-if="document_replace_success"
          class="requestor-portal-empty border mb-3"
        >
          {{ document_replace_success }}
        </div>
        <section
          v-if="selected_document.verification_label || selected_document.verification_comment"
          class="requestor-portal-empty border mb-3"
        >
          <div class="requestor-portal-label text-uppercase mb-2">Review outcome</div>
          <div class="requestor-portal-copy mb-2">Status: {{ selected_document.verification_label || 'Pending review' }}</div>
          <div v-if="selected_document.verification_comment" class="requestor-portal-copy">
            Comment : {{ selected_document.verification_comment }}
          </div>
        </section>
        <div v-if="can_preview_document(selected_document)" class="requestor-portal-preview-frame border">
          <img
            v-if="is_image_document(selected_document)"
            :src="selected_document.url"
            :alt="selected_document.title"
            class="requestor-portal-preview-image"
          >
          <iframe
            v-else-if="is_pdf_document(selected_document)"
            :src="selected_document.url"
            :title="selected_document.title"
            class="requestor-portal-preview-iframe"
          ></iframe>
        </div>
        <div v-else class="requestor-portal-empty border">
          This document type cannot be previewed in the browser. Use Download to open it.
        </div>
      </div>
    </aside>
  </div>

  <input
    ref="replacementDocumentInput"
    type="file"
    class="d-none"
    @change="replace_selected_document"
  >
  <input
    ref="caseDocumentUploadInput"
    type="file"
    class="d-none"
    @change="upload_case_document"
  >
</div>
